import React, { useState, useEffect } from 'react';
import './LiveBoard.css';

const LiveBoard = () => {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchFlights();
    const interval = setInterval(fetchFlights, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchFlights = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/liveboard');
      const data = await response.json();

      if (data.success) {
        setFlights(data.data);
        setLastUpdate(new Date());
      } else {
        setError('Не удалось загрузить данные табло');
      }
    } catch (err) {
      console.error('Ошибка загрузки:', err);
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  // Функция для открытия деталей рейса
  const handleFlightClick = (flightNumber) => {
    if (window.openFlightDetails) {
      window.openFlightDetails(flightNumber);
    } else {
      console.warn('⚠️ openFlightDetails не загружен');
    }
  };

  if (loading && flights.length === 0) {
    return (
      <div className="liveboard-loading">
        <div className="spinner"></div>
        <p>Загрузка онлайн-табло...</p>
      </div>
    );
  }

  return (
    <div className="liveboard">
      <div className="liveboard-header">
        <h2 className="liveboard-title">
          <i className="fa fa-television"></i>
          Онлайн-табло
        </h2>
        <div className="liveboard-update">
          <i className="fa fa-refresh"></i>
          <span>Обновлено: {lastUpdate ? lastUpdate.toLocaleTimeString('ru-RU') : '—'}</span>
          <span className="data-source-badge live">
            <i className="fa fa-cloud"></i> live
          </span>
          <button onClick={fetchFlights} className="refresh-btn" title="Обновить">
            <i className="fa fa-repeat"></i>
          </button>
        </div>
      </div>

      {error && (
        <div className="liveboard-error">
          <i className="fa fa-exclamation-circle"></i>
          <p>{error}</p>
          <button onClick={fetchFlights} className="retry-btn">Повторить</button>
        </div>
      )}

      {!error && flights.length === 0 && (
        <div className="liveboard-empty">
          <i className="fa fa-plane"></i>
          <p>Нет активных рейсов</p>
        </div>
      )}

      {flights.length > 0 && (
        <>
          {/* Десктопная таблица */}
          <div className="liveboard-table-container desktop-table">
            <table className="liveboard-table">
              <thead>
                <tr>
                  <th>Рейс</th>
                  <th>Оператор</th>
                  <th>Откуда</th>
                  <th>Куда</th>
                  <th>Статус</th>
                  <th>Время</th>
                </tr>
              </thead>
              <tbody>
                {flights.map((flight, index) => (
                  <tr 
                    key={`${flight.number}-${index}`} 
                    className={`flight-row status-${flight.status_class} clickable`}
                    onClick={() => handleFlightClick(flight.number)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="flight-number">
                      {flight.number || '—'}
                      {flight.operator?.codeshare && (
                        <span className="codeshare-badge" title={`Код-шеринг с ${flight.operator.partner}`}>
                          <i className="fa fa-handshake-o"></i> CS
                        </span>
                      )}
                    </td>
                    <td className="flight-operator">
                      {flight.operator && (
                        <span className={`operator-badge operator-${flight.operator.code}`}>
                          {flight.operator.name}
                        </span>
                      )}
                    </td>
                    <td className="flight-departure">
                      <span className="airport-name">{flight.dep_name || flight.dep_code}</span>
                    </td>
                    <td className="flight-arrival">
                      <span className="airport-name">{flight.arr_name || flight.arr_code}</span>
                    </td>
                    <td className="flight-status">
                      <span className={`status-badge ${flight.status_class}`}>
                        {flight.display_status}
                      </span>
                    </td>
                    <td className="flight-time">{flight.time || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Мобильные карточки */}
          <div className="mobile-liveboard-list">
            {flights.map((flight, index) => (
              <div 
                key={`${flight.number}-${index}`}
                className={`mobile-flight-card status-${flight.status_class} clickable`}
                onClick={() => handleFlightClick(flight.number)}
                style={{ cursor: 'pointer' }}
              >
                <div className="mobile-flight-header">
                  <div className="mobile-flight-number">
                    {flight.number || '—'}
                    {flight.operator?.codeshare && (
                      <span className="mobile-codeshare-badge" title={`Код-шеринг с ${flight.operator.partner}`}>
                        <i className="fa fa-handshake-o"></i> CS
                      </span>
                    )}
                  </div>
                  <div className="mobile-flight-status">
                    <span className={`status-badge-mobile ${flight.status_class}`}>
                      {flight.display_status}
                    </span>
                  </div>
                </div>

                <div className="mobile-operator">
                  {flight.operator && (
                    <span className={`operator-badge operator-${flight.operator.code}`}>
                      <i className="fa fa-building"></i> {flight.operator.name}
                    </span>
                  )}
                </div>

                <div className="mobile-route">
                  <div className="mobile-airport">
                    <div className="mobile-airport-label">Откуда</div>
                    <div className="mobile-airport-name">{flight.dep_name || flight.dep_code}</div>
                  </div>
                  <div className="mobile-route-arrow">
                    <i className="fa fa-arrow-right"></i>
                  </div>
                  <div className="mobile-airport">
                    <div className="mobile-airport-label">Куда</div>
                    <div className="mobile-airport-name">{flight.arr_name || flight.arr_code}</div>
                  </div>
                </div>

                <div className="mobile-flight-time">
                  <i className="fa fa-clock-o"></i>
                  <span>{flight.time || '—'}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LiveBoard;